import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Radio, Pencil, Trash2, X, Plus } from "lucide-react";
import api, { apiError } from "../lib/api";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const ACTIONS = ["joined Student", "started a Pro trial", "went Lifetime", "joined Pro"];

function EntryForm({ initial, onSave, onClose }) {
  const [f, setF] = useState({ name: "", location: "", action: "started a Pro trial", active: true, ...initial });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await onSave({ name: f.name, location: f.location, action: f.action, active: f.active });
      onClose();
    } catch (e2) { toast.error(apiError(e2)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-md my-8 space-y-4" data-testid="social-proof-form-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{initial?.id ? "Edit entry" : "New entry"}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
        </div>
        <div><label className="text-xs font-semibold text-slate-500">First name</label>
          <input data-testid="sp-form-name" className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></div>
        <div><label className="text-xs font-semibold text-slate-500">Location (e.g. "Manchester, UK")</label>
          <input data-testid="sp-form-location" className={inputCls} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></div>
        <div><label className="text-xs font-semibold text-slate-500">Action</label>
          <select data-testid="sp-form-action" className={inputCls} value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })}>
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select></div>
        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
          <input type="checkbox" data-testid="sp-form-active" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-4 h-4 accent-emerald-600" /> Active (included in rotation)
        </label>
        <button data-testid="sp-form-save" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 rounded-xl text-sm transition-colors">Save entry</button>
      </form>
    </div>
  );
}

export default function AdminSocialProof() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/social-proof").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async (body) => {
    if (editing?.id) await api.put(`/admin/social-proof/${editing.id}`, body);
    else await api.post("/admin/social-proof", body);
    toast.success("Saved");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/admin/social-proof/${id}`);
    load();
  };

  const toggleEnabled = async () => {
    await api.put("/admin/social-proof/settings", { enabled: !data.enabled });
    toast.success(data.enabled ? "Widget switched off" : "Widget switched on");
    load();
  };

  if (!data) return <p className="text-slate-400 p-8">Loading…</p>;
  return (
    <div data-testid="admin-social-proof">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display font-bold text-xl flex items-center gap-2"><Radio size={20} /> Signup Notifications</h2>
          <p className="text-sm text-slate-500 mt-1">Real signups from the last 48 hours always show first; these entries fill the quiet periods.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="sp-toggle-enabled" onClick={toggleEnabled}
            className={`text-sm font-bold px-4 py-2.5 rounded-xl transition-colors ${data.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
            {data.enabled ? "Widget: ON" : "Widget: OFF"}
          </button>
          <button data-testid="sp-new-entry-btn" onClick={() => setEditing({})}
            className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-colors">
            <Plus size={16} /> New entry
          </button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100">
        {data.entries.map((e) => (
          <div key={e.id} data-testid={`sp-entry-${e.id}`} className="flex items-center gap-3 px-5 py-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800">{e.name}{e.location ? <span className="text-slate-400 font-normal"> — {e.location}</span> : null}</p>
              <p className="text-xs text-slate-500">{e.action}</p>
            </div>
            {!e.active && <span className="text-[10px] font-bold uppercase bg-slate-100 text-slate-500 px-2 py-1 rounded-full">Inactive</span>}
            <button data-testid={`sp-edit-${e.id}`} onClick={() => setEditing(e)} className="text-slate-400 hover:text-emerald-700 p-1.5"><Pencil size={15} /></button>
            <button data-testid={`sp-delete-${e.id}`} onClick={() => remove(e.id)} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      {editing && <EntryForm initial={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}
